import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import NoData from 'common/NoData';
import PageSizer from 'common/PageSizer';
import Pagination from 'common/Pagination';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { copySuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';

import { makeActiveElementsConfig, PAGE_KEY } from './activeElementsConfigFactory';
import FiltersWorkAssignments from './components/Filters';
import WorkAssignmentCard from './components/WorkAssignmentCard';
import { WorkAssignmentSkeletonList } from './components/WorkAssignmentCardSkeleton';
import WorkAssignmentsTabs from './components/WorkAssignmentsTabs';
import { useFilterQueryParams } from './useFilterQueryParams';
import { WorkAssignmentsStore } from './WorkAssignmentsStore';

import styles from './styles.module.css';

const sendCopyOk = () => notificationStore.setNotification(copySuccess);

export const WorkAssignmentsList = observer(function WorkAssignmentsList() {
	const [store] = useState(() => new WorkAssignmentsStore());
	const [activeActionMenuId, setActiveActionMenuId] = useState(null);
	const active = useActiveElements();

	useFilterQueryParams(store);

	useEffect(() => {
		store.start();

		return () => {
			store.destroy();
		};
	}, [store]);

	useEffect(() => {
		const activeConfig = makeActiveElementsConfig({
			onBreakdownCreated: (breakdown) => store.upsertBreakdownByGroupId(breakdown),
		});
		active.mountForPage(PAGE_KEY, activeConfig);

		return () => active.cleanupPage(PAGE_KEY);
	}, [active, store]);

	const {
		assignments,
		relatedAssignments,
		isLoading,
		isError,
		activePage,
		currentPaginationInfo,
		setActivePage,
	} = store;

	const visibleAssignments = relatedAssignments ?? assignments;
	const assignmentsToRender = !isLoading && visibleAssignments.length > 0 ? visibleAssignments : [];

	return (
		<PageSizer>
			<div className={styles.filter}>
				<FiltersWorkAssignments store={store} />
			</div>

			<div className={styles.tabs}>
				<WorkAssignmentsTabs store={store} />
			</div>

			<div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
				{isLoading && <WorkAssignmentSkeletonList />}

				{assignmentsToRender.map((assignment, index) =>
					(() => {
						const assignmentKey = assignment?.id ?? assignment?.run?.id ?? `assignment-${index}`;
						const sgId = assignment?.sequenceGroupId ?? null;

						return (
							<WorkAssignmentCard
								key={assignmentKey}
								assignment={assignment}
								isActionMenuActive={activeActionMenuId === assignmentKey}
								isExpanded={store.isExpandedByGroupId(sgId)}
								onCopyRideId={sendCopyOk}
								onReconcileSuccess={() => store.fetchAssignments()}
								onShowRelatedAssignments={(payload) => store.prefillRelatedAssignments(payload)}
								onActionMenuToggle={(nextState) =>
									setActiveActionMenuId(nextState ? assignmentKey : null)
								}
								onToggleExpanded={() => store.toggleExpandedByGroupId(sgId)}
							/>
						);
					})()
				)}

				{!isLoading && assignmentsToRender.length === 0 && (
					<NoData
						customHeader="No assignments match the selected filters!"
						customMessage="Please adjust the selected filters."
					/>
				)}

				{isError && <NoData />}
			</div>
			<div className={styles.pagination}>
				<Pagination
					activePage={activePage}
					setActivePage={setActivePage}
					pageCount={currentPaginationInfo}
				/>
			</div>
		</PageSizer>
	);
});
